import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "input", "results", "badge", "result", "trigger"]
  static values = { url: String }

  connect() {
    this._timeout = null
    this._abortController = null
    this._activeIndex = -1
    this._isOpen = false
    this._isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgentData?.platform || navigator.platform)

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = this._isMac ? "\u2318K" : "Ctrl+K"
    }
  }

  disconnect() {
    if (this._timeout) clearTimeout(this._timeout)
    if (this._abortController) this._abortController.abort()
  }

  handleKeydown(event) {
    const modifierPressed = this._isMac ? event.metaKey : event.ctrlKey
    if (modifierPressed && event.key === "k") {
      event.preventDefault()
      this._isOpen ? this.close() : this.open()
    }
  }

  open() {
    this._isOpen = true
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this._activeIndex = -1

    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    this._isOpen = false
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this._activeIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")

    if (this._timeout) clearTimeout(this._timeout)
    if (this._abortController) this._abortController.abort()

    if (this.hasTriggerTarget) this.triggerTarget.focus()
  }

  search() {
    if (this._timeout) clearTimeout(this._timeout)

    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this._activeIndex = -1
      this.inputTarget.setAttribute("aria-expanded", "false")
      return
    }

    this._timeout = setTimeout(() => this._fetchResults(query), 300)
  }

  navigate(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === "Tab") {
      event.preventDefault()
      this.inputTarget.focus()
      return
    }

    const results = this.resultTargets
    if (results.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this._activeIndex = (this._activeIndex + 1) % results.length
      this._highlightResult()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this._activeIndex = (this._activeIndex - 1 + results.length) % results.length
      this._highlightResult()
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this._activeIndex >= 0 && this._activeIndex < results.length) {
        results[this._activeIndex].click()
      }
    }
  }

  selectResult() {
    this.close()
  }

  async _fetchResults(query) {
    if (this._abortController) this._abortController.abort()
    this._abortController = new AbortController()

    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-CSRF-Token": csrfToken },
        signal: this._abortController.signal
      })

      if (!this._isOpen) return

      if (response.redirected || !response.ok) {
        this.close()
        if (response.redirected) window.location.reload()
        return
      }

      const html = await response.text()
      if (!this._isOpen) return

      this.resultsTarget.innerHTML = html
      this._activeIndex = -1
      this.inputTarget.setAttribute("aria-expanded", this.resultTargets.length > 0 ? "true" : "false")
    } catch (error) {
      if (error.name !== "AbortError") {
        this.close()
      }
    }
  }

  _highlightResult() {
    const results = this.resultTargets
    results.forEach((el, i) => {
      if (i === this._activeIndex) {
        el.classList.add("bg-primary-50", "text-primary-700")
        el.classList.remove("hover:bg-neutral-50")
        el.setAttribute("aria-selected", "true")
        el.scrollIntoView({ block: "nearest" })
        this.inputTarget.setAttribute("aria-activedescendant", el.id)
      } else {
        el.classList.remove("bg-primary-50", "text-primary-700")
        el.classList.add("hover:bg-neutral-50")
        el.setAttribute("aria-selected", "false")
      }
    })
  }
}
